import React, { useState } from 'react'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import { Select } from '../ui/Select'
import { GovernanceInlineFormPanel } from './GovernanceInlineFormPanel'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

interface CreatePolicyModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    title: string
    code?: string
    type: string
    status: string
    department?: string
    ownerName?: string
    ownerEmail?: string
    effectiveFrom?: string
    effectiveTo?: string
  }) => Promise<void>
}

const CreatePolicyModal: React.FC<CreatePolicyModalProps> = ({ open, onClose, onSubmit }) => {
  const { t } = useI18n()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    code: '',
    type: 'policy',
    status: 'Draft',
    department: '',
    ownerName: '',
    ownerEmail: '',
    effectiveFrom: '',
    effectiveTo: ''
  })

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) return

    try {
      setLoading(true)
      await onSubmit({
        ...formData,
        code: formData.code || undefined,
        department: formData.department || undefined,
        ownerName: formData.ownerName || undefined,
        ownerEmail: formData.ownerEmail || undefined,
        effectiveFrom: formData.effectiveFrom || undefined,
        effectiveTo: formData.effectiveTo || undefined
      })
      // Reset form
      setFormData({
        title: '',
        code: '',
        type: 'policy',
        status: 'Draft',
        department: '',
        ownerName: '',
        ownerEmail: '',
        effectiveFrom: '',
        effectiveTo: ''
      })
      onClose()
    } catch (error) {
      console.error('[CreatePolicyModal] Error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <GovernanceInlineFormPanel
      title={t('governance.policies.create', 'Create New Policy')}
      onClose={onClose}
      footer={
        <>
          <Button type="button" color="outline" size="small" onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form="governance-create-policy-form"
            color="primary"
            size="small"
            disabled={loading || !formData.title.trim()}
          >
            {loading ? t('common.saving', 'Saving…') : t('common.create', 'Create')}
          </Button>
        </>
      }
    >
      <form id="governance-create-policy-form" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('governance.policies.form.title', 'Title')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={listStyles.formInput}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.form.code', 'Code')}
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="ISMS-001"
                className={listStyles.formInput}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.form.type', 'Type')} <span className="text-red-500">*</span>
              </label>
              <Select<string>
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:border-gray-600 dark:bg-neutral-800"
                options={[
                  { value: 'policy', label: t('governance.policies.form.typePolicy', 'Policy') },
                  { value: 'procedure', label: t('governance.policies.form.typeProcedure', 'Procedure') },
                  { value: 'guideline', label: t('governance.policies.form.typeGuideline', 'Guideline') },
                  { value: 'instruction', label: t('governance.policies.form.typeInstruction', 'Instruction') },
                ]}
                value={formData.type || null}
                onChange={(v) => setFormData({ ...formData, type: v ?? 'policy' })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.form.status', 'Status')}
              </label>
              <Select<string>
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:border-gray-600 dark:bg-neutral-800"
                options={[
                  { value: 'Draft', label: t('governance.policies.form.statusDraft', 'Draft') },
                  { value: 'UnderReview', label: t('governance.policies.form.statusUnderReview', 'Under Review') },
                  { value: 'Approved', label: t('governance.policies.form.statusApproved', 'Approved') },
                  { value: 'Deprecated', label: t('governance.policies.form.statusDeprecated', 'Deprecated') },
                ]}
                value={formData.status || null}
                onChange={(v) => setFormData({ ...formData, status: v ?? 'Draft' })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.form.department', 'Department')}
              </label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className={listStyles.formInput}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.form.ownerName', 'Owner Name')}
              </label>
              <input
                type="text"
                value={formData.ownerName}
                onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                className={listStyles.formInput}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.form.ownerEmail', 'Owner Email')}
              </label>
              <input
                type="email"
                value={formData.ownerEmail}
                onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
                className={listStyles.formInput}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.form.effectiveFrom', 'Effective From')}
              </label>
              <input
                type="date"
                value={formData.effectiveFrom}
                onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
                className={listStyles.formInput}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.form.effectiveTo', 'Effective To')}
              </label>
              <input
                type="date"
                value={formData.effectiveTo}
                onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
                className={listStyles.formInput}
              />
            </div>
          </div>

        </form>
    </GovernanceInlineFormPanel>
  )
}

export default CreatePolicyModal


